import Vue from 'vue'
import * as Sentry from '@sentry/vue'
import router from './router'
import { version as appVersion } from '../package.json'

const SENTRY_DSN =
  process.env.VUE_APP_SENTRY_DSN ||
  'https://1e75da7a1f291e7d9f865aee4c270358@o4508246174531584.ingest.de.sentry.io/4511925454372944'

function isIosWebView() {
  return !!(window.webkit && window.webkit.messageHandlers)
}

function isAndroidWebView() {
  return typeof window.AndroidJSInterface !== 'undefined'
}

function getClientPlatform() {
  if (isIosWebView()) return 'ios-webview'
  if (isAndroidWebView()) return 'android-webview'
  return 'browser'
}

function getServiceWorkerState() {
  if (!('serviceWorker' in navigator)) return 'unsupported'
  if (navigator.serviceWorker.controller) return 'controlled'
  return 'uncontrolled'
}

/**
 * Initialize Sentry error monitoring for Vue 2 (incl. iOS/Android WebViews).
 * Release + platform tags help diagnose stale/cached JS bundles.
 */
export function initSentry() {
  if (!SENTRY_DSN) return

  const clientPlatform = getClientPlatform()

  Sentry.init({
    Vue,
    dsn: SENTRY_DSN,
    release: `sportlerplus-frontend@${appVersion}`,
    environment: process.env.NODE_ENV,
    // Collect from production WebViews/browsers; skip local noise by default
    enabled: process.env.NODE_ENV === 'production' || process.env.VUE_APP_SENTRY_ENABLE === 'true',

    // Transient WebView/network noise — not actionable app bugs
    ignoreErrors: [
      'Failed to fetch',
      'Load failed',
      'NetworkError when attempting to fetch resource.',
      'Network request failed',
      /Failed to fetch/i,
      /Load failed/i,
      /NetworkError when attempting to fetch resource/i,
      /Unable to establish a connection with the popup/i,
      /auth\/popup-closed-by-user/i,
      /auth\/cancelled-popup-request/i,
      /Avoided redundant navigation to current location/i,
      'NavigationDuplicated',
      'NavigationCancelled',
      'NavigationAborted',
      /Navigation cancelled from/i,
      /Navigation aborted from/i,
      // Firebase Auth/Firestore IndexedDB: WebView closes IDB while a transaction is pending
      /Failed to execute 'transaction' on 'IDBDatabase'/i,
      /The database connection is closing/i,
      // Expected media policy (iOS WKWebView / autoplay without gesture)
      'NotAllowedError',
      /The request is not allowed by the user agent/i,
      'AbortError',
      /play\(\) request was interrupted/i,
      // Stale webpack chunks after deploy / SW cache mismatch — not app bugs
      /Loading CSS chunk .+ failed/i,
      /Loading chunk [\d]+ failed/i,
      'ChunkLoadError',
      /Failed to fetch dynamically imported module/i,
      /Importing a module script failed/i,
    ],

    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],

    // Keep tracing light; Replay disabled (DOM recording cost in WebViews)
    tracesSampleRate: 0.1,

    initialScope: {
      tags: {
        client_platform: clientPlatform,
        ios_webview: String(isIosWebView()),
        android_webview: String(isAndroidWebView()),
        app_version: appVersion,
        service_worker: getServiceWorkerState(),
      },
    },

    beforeSend(event, hint) {
      const error = hint && hint.originalException
      const message = (error && error.message) || (event.exception && event.exception.values && event.exception.values[0] && event.exception.values[0].value) || ''

      // Flag classic stale-bundle / chunk-mismatch failures (if not already ignored)
      if (
        /Loading CSS chunk .+ failed/i.test(message) ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /ChunkLoadError/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
      ) {
        event.tags = event.tags || {}
        event.tags.stale_assets = 'true'
      }

      event.tags = event.tags || {}
      event.tags.client_platform = getClientPlatform()
      event.tags.service_worker = getServiceWorkerState()

      return event
    },
  })
}

export { isIosWebView, isAndroidWebView, getClientPlatform }
